import { useEffect, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import {
  clearWebPushMessage, disableWebPush, dismissWebPushInvitation, enableWebPush,
  getServerWebPushState, getWebPushState, listenForWebPushOpens, queueWebPushOpen,
  refreshWebPush, setWebPushAccount, subscribeWebPush,
} from "../../../services/webPush";
import styles from "./PushNotifications.module.scss";

const usePushState = () => useSyncExternalStore(subscribeWebPush, getWebPushState, getServerWebPushState);

export const PushNotifications = () => {
  const location = useLocation();
  const state = usePushState();

  useEffect(() => {
    const syncAccount = () => {
      try {
        const auth = JSON.parse(localStorage.getItem("mamiVendorXAuth") || "{}");
        const id = String(auth?.userProfile?.id || "");
        const accessToken = auth?.access_token || "";
        setWebPushAccount(id && accessToken ? { id, accessToken } : null);
      } catch { setWebPushAccount(null); }
    };
    syncAccount();
    window.addEventListener("storage", syncAccount);
    return () => window.removeEventListener("storage", syncAccount);
  }, [location.key]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") refreshWebPush(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  useEffect(() => listenForWebPushOpens(), []);

  const invite = state.accountId && !state.dismissed && (state.status === "off" || state.status === "error");
  return <>
    {invite && <aside className={styles.invite} aria-label="Enable notifications">
      <div><strong>Get MamiMarkets notifications</strong><p>Allow browser alerts for updates while you use this device.</p></div>
      <button type="button" onClick={() => void enableWebPush()}>Enable notifications</button>
      <button type="button" className={styles.dismiss} onClick={dismissWebPushInvitation}>Not now</button>
    </aside>}
    {state.notification && <div className={styles.toast} role="status">
      <button type="button" className={styles.toastContent} onClick={() => {
        queueWebPushOpen(state.notification?.notificationId);
        clearWebPushMessage();
      }} aria-label={`Open notification: ${state.notification.title}`}>
        <strong>{state.notification.title}</strong><p>{state.notification.body}</p>
      </button>
      <button type="button" aria-label="Dismiss notification" onClick={clearWebPushMessage}>×</button>
    </div>}
  </>;
};

export const PushSettingsControl = () => {
  const state = usePushState();
  const enabled = state.status === "enabled";
  const disabled = state.status === "working" || state.status === "unavailable" || !state.accountId;
  return <section className={styles.settings} aria-label="Browser notifications">
    <h2>Browser notifications</h2>
    <p>Receive MamiMarkets updates in this browser when you are signed in.</p>
    <p className={styles.status}>{state.status === "unavailable" ? "Not available in this browser or environment." :
      state.status === "denied" ? "Blocked by browser settings. Allow notifications for this site, then try again." :
      enabled ? "Enabled on this browser." : state.status === "working" ? "Updating…" : "Off on this browser."}</p>
    {state.message && <p className={styles.error} role="alert">{state.message}</p>}
    <button type="button" disabled={disabled || state.status === "denied"} onClick={() => void (enabled ? disableWebPush() : enableWebPush())}>
      {enabled ? "Disable notifications" : "Enable notifications"}
    </button>
  </section>;
};
